import {IDictionary} from '@/shared/models/interface/dictionary-model';
import {ActionTree} from 'vuex';
import {IFilterItem} from '../models/filter-item-model';
import {SearchResponse} from "../models/search-reponse-model";
import queryStringService from '../services/query-string-service';
import {State} from './state';

export default <ActionTree<State, any>>{
    async loadData({commit, state, dispatch}, [append = true]) {

        if (state.config.shouldDelaySearch && (!state.searchQuery || state.searchQuery.trim() === "")) {
            // Skipping data load because search query is empty.
            commit('setSearchResultsCount', 0);
            commit(append ? 'addSearchResults' : 'setSearchResults', []);
            commit('setSearchLoading', false);
            
            if (!state.isInitialized) {
                setTimeout(() => {
                    commit('setIsInitialized', true);
                    dispatch('getSearchUrlQueryString');
                }, 500);
            }

            return;
        }

        commit('setSearchLoading', true);
        commit('setSearchFailed', false);
        dispatch('setFilterQuery');
        dispatch('setSearchQuery', [append]);
        
        await state.searchService.loadData()
            .then((response: SearchResponse) => {
                commit('setInitialSearchCallExecuted', true)
                commit('setSearchResultsCount', response.count)
                commit(append ? 'addSearchResults' : 'setSearchResults', response.results)
                dispatch('filter/handleSearchFilters', [response.filters])
            }).catch((error: Error) => {
                console.error(error)
                commit('setSearchFailed', true)
            }).finally(() => {
                commit('setSearchLoading', false)
                dispatch('setSearchUrlQueryString')

                if (!state.isInitialized) {
                    // Make sure we're fully ready before we set isInitialized
                    // to true, avoids the watchDebounce firing upon the initial
                    // change of the searchQuery input upon load. The debounce is
                    // also 500ms, so we should be ready when the user stops typing.
                    setTimeout(() => {
                        commit('setIsInitialized', true);
                        dispatch('getSearchUrlQueryString');
                    }, 500);
                }
            })
    },

    setFilterQuery({state, getters}) {
        state.searchService.setFilterQuery(
            state.fixedFilters,
            getters['scope/activeScopes'],
            combineDictionaries(getters['filter/groupedActiveFilters'], state.UserFilterOverride)
        );

        // Combine the user defined filters with the override filters.
        function combineDictionaries(
            groupedActiveFilters: IDictionary<IFilterItem[]>,
            userFilterOverride: IDictionary<IFilterItem[]>
        ): IDictionary<IFilterItem[]> {

            const result: IDictionary<IFilterItem[]> = {...groupedActiveFilters};

            for (const key in userFilterOverride) {
                result[key] = userFilterOverride[key];
            }

            return result;
        }
    },

    setSearchQuery({commit, state}, [append = true]) {
        commit('setPageNumber', append ? state.pageNumber + 1 : 0)

        state.searchService.setSearchQuery(
            state.config.order,
            state.searchQuery,
            state.searchResultSkip * state.pageNumber,
            state.searchResultLimit
        );
    },

    setSearchUrlQueryString({state, getters}) {
        let scopes: string[] = getters['scope/activeScopes']?.map((x: IFilterItem) => x.DisplayName) ?? [];
        let filters: IDictionary<string[]> = {};

        Object.entries(getters['filter/groupedActiveFilters']).map(([filterGroup, filterItems]) => {
            filters[filterGroup] = (filterItems as IFilterItem[]).map((x: IFilterItem) => x.DisplayName);
        });

        queryStringService.setQueryString(state.searchQuery, scopes, filters);
    },

    getSearchUrlQueryString({commit, getters}) {
        commit('setSearchQuery', queryStringService.getSearch());

        queryStringService.getScopes()?.forEach((scopeName: string) => {
            let scopeItem = getters['scope/scopeItem'](scopeName);
            commit('scope/setActiveScope', scopeItem?.FilterCodeName);
        });

        queryStringService.getFilters()?.forEach((name: string) => {
            let filterItem = getters['filter/singleFilterItem'](name);
            commit('filter/setActiveFilter', filterItem.FilterCodeName);
            commit('filter/setActiveFilterGroup', filterItem.FilterGroup);
        });
    },

    init({commit}, props) {
        commit('setIsInitialized', false);

        try {
            commit('setConfig', props.searchSettings);
            commit('setImageFallback', props.imageFallback)
            commit('filter/setUserFilters', props.userFilters);
            commit('scope/setScopeFilters', props.scopeFilter);
            commit('scope/setScopeLinks', props.scopeLinks);
            commit('setFixedFilters', props.fixedFilters);
            commit('setUserFilterOverride', props.userFilterOverride);
        } catch (error) {
            console.error(error);
        }
    }
}